
import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bot, User, Send, TrendingUp, Users, DollarSign, Activity, Sparkles } from "lucide-react";

interface Message {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
}

interface AIInsightsPanelProps {
  title: string;
  dataType: 'members' | 'transactions';
  sampleQuestions: string[];
  data?: any[];
}

export function AIInsightsPanel({ title, dataType, sampleQuestions, data = [] }: AIInsightsPanelProps) {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'ai',
      content: `Hello! I'm your AI analytics assistant for ${dataType}. I can help you analyze patterns, trends, and insights from your data. What would you like to know?`,
      timestamp: new Date()
    }
  ]);
  const [isGenerating, setIsGenerating] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [messages]);

  const generateAIResponse = (userInput: string): string => {
    const input = userInput.toLowerCase();
    
    if (dataType === 'members') {
      if (input.includes('total') && input.includes('member')) {
        return `Based on the current data, you have **${data.length} total members** across all programs. Here's a breakdown:
        
• **Active Members**: ${data.filter(m => m.status === 'Active').length}
• **Average Points Balance**: ${Math.round(data.reduce((sum, m) => sum + (m.pointsBalance || 0), 0) / data.length)} points
• **Most Popular Program**: ${data.length > 0 ? data[0].programName : 'N/A'}
• **Recent Joiners**: ${data.filter(m => new Date(m.joinDate) > new Date(Date.now() - 30*24*60*60*1000)).length} members in last 30 days

Would you like me to analyze any specific aspect of your member data?`;
      }
      
      if (input.includes('trend') || input.includes('growth')) {
        return `**Member Growth Trends:**

📈 **Growth Rate**: +12% this month
🎯 **Retention Rate**: 89% (excellent!)
⭐ **Engagement Score**: 7.8/10
🔄 **Churn Risk**: 3 members need attention

**Key Insights:**
• Peak registration time: Tuesday 2-4 PM
• Members with >1000 points have 95% retention
• Email campaigns increase engagement by 34%

What specific trend would you like me to dive deeper into?`;
      }
      
      if (input.includes('engagement') || input.includes('active')) {
        return `**Member Engagement Analysis:**

🔥 **Highly Engaged**: ${Math.round(data.length * 0.3)} members (>5 activities/month)
⚡ **Moderately Engaged**: ${Math.round(data.length * 0.5)} members (2-4 activities/month)
😴 **Low Engagement**: ${Math.round(data.length * 0.2)} members (<2 activities/month)

**Recommendations:**
• Send re-engagement campaigns to low-activity members
• Reward highly engaged members with bonus points
• Create tier-specific challenges to boost participation`;
      }
    } else {
      if (input.includes('revenue') || input.includes('total') || input.includes('amount')) {
        const totalAmount = data.reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0);
        return `**Revenue & Transaction Analysis:**

💰 **Total Transaction Value**: $${totalAmount.toFixed(2)}
📊 **Average Transaction**: $${(totalAmount / data.length).toFixed(2)}
🔄 **Transaction Count**: ${data.length} transactions
📈 **Growth Rate**: +18% vs last month

**Top Performing:**
• Highest transaction: $${Math.max(...data.map(tx => parseFloat(tx.amount || 0))).toFixed(2)}
• Most active merchant: ${data.length > 0 ? data[0].merchant : 'N/A'}
• Peak transaction time: Weekends 6-8 PM`;
      }
      
      if (input.includes('trend') || input.includes('pattern')) {
        return `**Transaction Patterns & Trends:**

📅 **Seasonal Trends**: 23% higher during holidays
⏰ **Peak Hours**: 6-8 PM weekdays, 2-4 PM weekends
🏪 **Top Categories**: Food & Beverage (34%), Retail (28%), Entertainment (19%)
💳 **Payment Preferences**: 68% mobile payments, 32% card

**Insights:**
• Friday transactions are 40% higher than Monday
• Members spend 25% more after earning bonus points
• Redemption rate increases 60% with personalized offers`;
      }
      
      if (input.includes('points') || input.includes('earn') || input.includes('redeem')) {
        const earnTransactions = data.filter(tx => tx.type === 'earn');
        const burnTransactions = data.filter(tx => tx.type === 'burn');
        return `**Points Economy Analysis:**

🟢 **Points Earned**: ${earnTransactions.reduce((sum, tx) => sum + Math.abs(parseInt(tx.points || 0)), 0)} points
🔴 **Points Redeemed**: ${burnTransactions.reduce((sum, tx) => sum + Math.abs(parseInt(tx.points || 0)), 0)} points
⚖️ **Earn/Burn Ratio**: ${(earnTransactions.length / (burnTransactions.length || 1)).toFixed(2)}:1

**Redemption Insights:**
• Average redemption: ${burnTransactions.length > 0 ? Math.round(burnTransactions.reduce((sum, tx) => sum + Math.abs(parseInt(tx.points || 0)), 0) / burnTransactions.length) : 0} points
• Most popular rewards: Gift cards (45%), Discounts (35%)
• Optimal points balance: 500-1500 points for best engagement`;
      }
    }
    
    // Generic response for other queries
    return `I understand you're asking about "${userInput}". Let me analyze the ${dataType} data for you.

Based on the current data patterns, I can see several interesting trends. However, to provide you with more specific insights, could you please:

• Be more specific about what metrics you'd like to analyze
• Mention any particular time period you're interested in
• Let me know if you want to compare different segments

Try asking about trends, growth rates, engagement levels, or specific performance metrics!`;
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentMessage = message;
    setMessage('');
    setIsGenerating(true);

    try {
      // Simulate AI processing
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const aiResponse = generateAIResponse(currentMessage);
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: aiResponse,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
      
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSampleQuestion = (question: string) => {
    setMessage(question);
  };

  return (
    <Card className="h-[600px] flex flex-col">
      <CardHeader className="flex-shrink-0 border-b">
        <CardTitle className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-blue-500" />
          {title}
          <Badge variant="secondary" className="ml-auto">
            AI Analytics
          </Badge>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="flex-1 flex flex-col p-0 min-h-0">
        {/* Messages Area */}
        <ScrollArea className="flex-1 p-4 min-h-0" ref={scrollAreaRef}>
          <div className="space-y-4 pr-4">
            {messages.map((msg) => (
              <div 
                key={msg.id} 
                className={`flex gap-3 ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.type === 'ai' && (
                  <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                )}
                
                <div className={`max-w-[80%] ${msg.type === 'user' ? 'order-first' : ''}`}>
                  <div 
                    className={`rounded-lg px-4 py-3 ${
                      msg.type === 'user' 
                        ? 'bg-blue-500 text-white ml-auto' 
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <div className="whitespace-pre-wrap text-sm leading-relaxed">
                      {msg.content}
                    </div>
                  </div>
                  <div className={`text-xs text-gray-500 mt-1 ${msg.type === 'user' ? 'text-right' : 'text-left'}`}>
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                
                {msg.type === 'user' && (
                  <div className="h-8 w-8 rounded-full bg-gray-500 flex items-center justify-center flex-shrink-0">
                    <User className="h-4 w-4 text-white" />
                  </div>
                )}
              </div>
            ))}
            
            {isGenerating && (
              <div className="flex gap-3 justify-start">
                <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div className="bg-gray-100 rounded-lg px-4 py-3">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
        
        {/* Sample Questions */}
        <div className="border-t p-3 bg-gray-50">
          <div className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
            <Sparkles className="h-3 w-3" />
            Quick Questions:
          </div>
          <div className="flex flex-wrap gap-1">
            {sampleQuestions.slice(0, 3).map((question, index) => (
              <Button 
                key={index}
                variant="outline" 
                size="sm"
                className="text-xs h-auto py-1 px-2"
                onClick={() => handleSampleQuestion(question)}
              >
                {question}
              </Button>
            ))}
          </div>
        </div>
        
        {/* Message Input */}
        <div className="border-t p-4 flex-shrink-0">
          <div className="flex gap-3">
            <Textarea 
              placeholder="Ask me about your data metrics and insights..." 
              className="flex-1 min-h-[60px] max-h-[120px] resize-none"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isGenerating}
            />
            <Button 
              onClick={handleSendMessage}
              disabled={isGenerating || !message.trim()}
              className="px-6"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
            <span>Press Enter to send, Shift+Enter for new line</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
